import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getReservationByReference } from "@/lib/reservations.functions";
import { CheckCircle2, Clock, AlertCircle, MapPin } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/booking-confirmed/$reference")({
  head: ({ params }) => ({
    meta: [
      { title: "Booking Confirmation — Plunkets Pub Dublin" },
      { name: "description", content: "Your Plunkets Pub Dublin reservation details." },
      { property: "og:title", content: "Booking Confirmation — Plunkets Pub Dublin" },
      { property: "og:description", content: "View your reservation at Plunkets Pub on Middle Abbey Street, Dublin." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://plunkets.ie/booking-confirmed/${encodeURIComponent(params.reference)}` },
      { property: "og:image", content: "https://plunkets.ie/og-default.jpg?v=3" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://plunkets.ie/og-default.jpg?v=3" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BookingConfirmedPage,
});

function BookingConfirmedPage() {
  const { reference } = useParams({ from: "/booking-confirmed/$reference" });
  const { data, isLoading } = useQuery({
    queryKey: ["reservation", reference],
    queryFn: () => getReservationByReference({ data: { reference } }),
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });

  const rawTable = (data as { table?: unknown } | null | undefined)?.table;
  const table = (Array.isArray(rawTable) ? rawTable[0] : rawTable) as
    | { label?: string; capacity?: number; zone?: { name?: string } | { name?: string }[] | null }
    | null
    | undefined;
  const zone = table
    ? Array.isArray(table.zone)
      ? table.zone[0]
      : table.zone
    : undefined;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="glass-nav !bg-background fixed top-0 z-50 w-full">
        <div className="mx-auto flex h-16 max-w-7xl items-center px-5 md:px-8">
          <Link to="/" className="font-display text-lg">Plunkets</Link>
        </div>
      </header>

      <section className="mx-auto max-w-2xl px-5 pb-24 pt-32 md:pt-40">
        <p className="text-xs uppercase tracking-[0.3em] text-primary">Reservation</p>
        <h1 className="mt-3 font-display text-4xl md:text-5xl">
          Reference <span className="text-gold-gradient">{reference}</span>
        </h1>

        {isLoading && <p className="mt-8 text-sm text-muted-foreground">Loading…</p>}

        {data && (
          <div className="mt-8 space-y-5 rounded-2xl border border-border bg-card/60 p-6">
            <StatusBadge status={data.status} />

            {data.status === "confirmed" && table?.label && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                <p className="text-xs uppercase tracking-widest text-emerald-400">
                  Your table
                </p>
                <p className="mt-1 font-display text-2xl">{table.label}</p>
                {zone?.name && (
                  <p className="text-sm text-muted-foreground">{zone.name}</p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  Please show this reference at the door — our staff will guide you to your table.
                </p>
              </div>
            )}

            {data.status === "confirmed" && !table?.label && (
              <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                Your reservation is confirmed. A specific table will be assigned when you arrive.
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Info label="Booking type" value={data.booking_type.replaceAll("_", " ")} />
              <Info label="When" value={new Date(data.starts_at).toLocaleString("en-IE", { timeZone: "Europe/Dublin" })} />
              <Info label="Duration" value={`${data.duration_minutes} min`} />
              <Info label="Party size" value={String(data.party_size)} />
              {data.preferred_area && <Info label="Area" value={data.preferred_area} />}
              {data.deposit_required && (
                <Info label="Deposit" value={`€${(data.deposit_amount_cents / 100).toFixed(2)}`} />
              )}
              {data.notes && (
                <div className="sm:col-span-2">
                  <Info label="Notes" value={data.notes} />
                </div>
              )}
            </div>
            <p className="flex items-center gap-2 pt-2 text-sm text-muted-foreground">
              <MapPin size={14} /> Plunkets Pub, Middle Abbey Street, Dublin 1
            </p>
          </div>
        )}

        {!isLoading && !data && (
          <p className="mt-8 text-sm text-muted-foreground">
            We couldn't find that reservation. Please contact us if you think this is an error.
          </p>
        )}

        <div className="mt-8">
          <Link to="/" className="text-sm text-primary hover:underline">
            ← Back to plunkets.ie
          </Link>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm">{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; icon: typeof Clock; color: string }> = {
    confirmed: { label: "Confirmed", icon: CheckCircle2, color: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
    pending_approval: { label: "Pending approval", icon: Clock, color: "text-amber-300 border-amber-500/30 bg-amber-500/10" },
    pending_payment: { label: "Pending deposit", icon: Clock, color: "text-amber-300 border-amber-500/30 bg-amber-500/10" },
    unavailable: { label: "Unavailable", icon: AlertCircle, color: "text-destructive border-destructive/30 bg-destructive/10" },
    cancelled: { label: "Cancelled", icon: AlertCircle, color: "text-muted-foreground border-border bg-muted/30" },
  };
  const m = map[status] ?? map.pending_approval;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${m.color}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
}
